import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatters';

export interface DiretoriaSimularModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  periodFrom: string;
  periodTo: string;
  periodLabel: string;
  ano: number;
  mes: number;
  criadas: number;
  canceladas: number;
  mensal: Array<{ name: string; novas: number; canceladas: number }>;
  tiposLocacao: Array<{ tipo: string; quantidade: number; canceladas: number }>;
}

interface ProjecaoContextoPayload {
  projecao: {
    projetadoConservador: number;
    projetadoBase: number;
    projetadoAgressivo: number;
    gapVsMetaCriadas: number | null;
    horizonteFimMes: string;
    runRateDiarioCriadas: number;
  };
  financeiro?: {
    ticketMedio: number;
    receitaPeriodoEstimada: number;
    projetadoConservador: number;
    projetadoBase: number;
    projetadoAgressivo: number;
    metaFinanceira: number | null;
    gapVsMetaFinanceiro: number | null;
  };
  sugestoesAcao: string[];
  metas: { locoesCriadas: number | null };
}

export const DiretoriaSimularModal: React.FC<DiretoriaSimularModalProps> = ({
  open,
  onClose,
  companyId,
  periodFrom,
  periodTo,
  periodLabel,
  ano,
  mes,
  criadas,
  canceladas,
  mensal,
  tiposLocacao,
}) => {
  const queryClient = useQueryClient();

  const { data: projecao, isLoading: loadingProjecao } = useQuery({
    queryKey: ['diretoria-projecao-contexto', companyId, periodFrom, periodTo, ano, mes],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyId) params.set('companyId', companyId);
      params.set('dataFrom', periodFrom);
      params.set('dataTo', periodTo);
      params.set('ano', String(ano));
      params.set('mes', String(mes));
      const res = await api.get(`/reports/diretoria/projecao-contexto?${params.toString()}`);
      return res.data.data as ProjecaoContextoPayload;
    },
    enabled: open,
    staleTime: 60_000,
  });

  const applyMeta = useMutation({
    mutationFn: async (valorMeta: number) => {
      await api.put('/reports/diretoria/metas', {
        companyId: companyId || undefined,
        ano,
        mes,
        locoesCriadas: valorMeta,
      });
    },
    onSuccess: () => {
      toast.success('Meta de locações aplicada');
      void queryClient.invalidateQueries({ queryKey: ['diretoria-metas'] });
      void queryClient.invalidateQueries({ queryKey: ['diretoria-projecao-contexto'] });
    },
    onError: () => toast.error('Falha ao aplicar meta'),
  });

  const handleApply = (valor: number) => {
    if (
      !window.confirm(
        `Aplicar meta de ${valor.toLocaleString('pt-BR')} locações para ${mes}/${ano}?`
      )
    ) {
      return;
    }
    applyMeta.mutate(valor);
  };

  if (!open) return null;

  const p = projecao?.projecao;
  const fin = projecao?.financeiro;
  const hasFinanceiro = (fin?.ticketMedio ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fechar"
        onClick={onClose}
      />
      <Card className="relative z-10 max-h-[90vh] w-full max-w-3xl overflow-hidden border-border shadow-xl">
        <CardHeader className="flex flex-row items-start justify-between gap-4 border-b border-border pb-4">
          <div>
            <CardTitle className="text-lg">Simular — histórico de locações</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Período {periodLabel}. Cenários até {p?.horizonteFimMes ?? 'fim do mês'} com base no
              run-rate.
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="max-h-[calc(90vh-5rem)] space-y-6 overflow-y-auto py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border/80 p-3">
              <p className="text-[11px] uppercase text-muted-foreground">Criadas</p>
              <p className="font-heading text-xl font-bold tabular-nums">{criadas.toLocaleString('pt-BR')}</p>
            </div>
            <div className="rounded-lg border border-border/80 p-3">
              <p className="text-[11px] uppercase text-muted-foreground">Canceladas</p>
              <p className="font-heading text-xl font-bold tabular-nums">
                {canceladas.toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="rounded-lg border border-border/80 p-3">
              <p className="text-[11px] uppercase text-muted-foreground">Turnover</p>
              <p className="font-heading text-xl font-bold tabular-nums">
                {criadas > 0 ? `${((canceladas / criadas) * 100).toFixed(1)}%` : '—'}
              </p>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">Cenários sugeridos</h3>
            {loadingProjecao ? (
              <p className="text-sm text-muted-foreground">Calculando projeção…</p>
            ) : p ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {(
                    [
                      ['Conservador', p.projetadoConservador],
                      ['Base', p.projetadoBase],
                      ['Agressivo', p.projetadoAgressivo],
                    ] as const
                  ).map(([label, valor]) => (
                    <div key={label} className="rounded-lg border border-border/80 p-3">
                      <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
                      <p className="font-heading text-lg font-bold tabular-nums">
                        {valor.toLocaleString('pt-BR')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        disabled={applyMeta.isPending}
                        onClick={() => handleApply(valor)}
                      >
                        Aplicar como meta
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Run-rate ~{p.runRateDiarioCriadas.toLocaleString('pt-BR')} locações/dia
                  {p.gapVsMetaCriadas != null
                    ? ` · gap vs meta atual: ${p.gapVsMetaCriadas > 0 ? '+' : ''}${p.gapVsMetaCriadas}`
                    : ''}
                  {projecao?.metas?.locoesCriadas != null
                    ? ` (meta ${projecao.metas.locoesCriadas})`
                    : ''}
                </p>
                {hasFinanceiro && fin ? (
                  <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
                    <p className="mb-2 text-xs font-semibold text-foreground">Projeção financeira</p>
                    <p className="mb-2 text-xs text-muted-foreground">
                      Ticket médio {formatCurrency(fin.ticketMedio)} · estim. período{' '}
                      {formatCurrency(fin.receitaPeriodoEstimada)}
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {(
                        [
                          ['Conservador', fin.projetadoConservador],
                          ['Base', fin.projetadoBase],
                          ['Agressivo', fin.projetadoAgressivo],
                        ] as const
                      ).map(([label, valor]) => (
                        <div key={label} className="rounded-md border border-border/60 bg-background p-2">
                          <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
                          <p className="font-heading text-sm font-bold tabular-nums">
                            {formatCurrency(valor)}
                          </p>
                        </div>
                      ))}
                    </div>
                    {fin.metaFinanceira != null ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Meta financeira {formatCurrency(fin.metaFinanceira)}
                        {fin.gapVsMetaFinanceiro != null
                          ? ` · gap ${fin.gapVsMetaFinanceiro >= 0 ? '+' : ''}${formatCurrency(fin.gapVsMetaFinanceiro)}`
                          : ''}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {projecao?.sugestoesAcao?.length ? (
                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {projecao.sugestoesAcao.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Não foi possível calcular cenários.</p>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">Histórico mensal</h3>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Mês</th>
                    <th className="px-3 py-2 text-right font-medium">Criadas</th>
                    <th className="px-3 py-2 text-right font-medium">Canceladas</th>
                  </tr>
                </thead>
                <tbody>
                  {mensal.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">
                        Sem histórico no período
                      </td>
                    </tr>
                  ) : (
                    mensal.map((row) => (
                      <tr key={row.name} className="border-t border-border/60">
                        <td className="px-3 py-2">{row.name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.novas}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.canceladas}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">Tipos de locação / contrato</h3>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Tipo</th>
                    <th className="px-3 py-2 text-right font-medium">Qtd</th>
                    <th className="px-3 py-2 text-right font-medium">Canceladas</th>
                  </tr>
                </thead>
                <tbody>
                  {tiposLocacao.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">
                        Sem tipos no período
                      </td>
                    </tr>
                  ) : (
                    tiposLocacao.map((row) => (
                      <tr key={row.tipo} className="border-t border-border/60">
                        <td className="px-3 py-2">{row.tipo}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.quantidade}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.canceladas}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
